import { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Avatar,
  Box,
  Typography,
  CircularProgress,
  FormHelperText
} from '@mui/material';

import { departmentService } from '../services/api';
import { getEmployeeFullName, getInitialsFromEmployee } from '../utils/names';
import { inStaffOnly } from '../utils/staffStatus';

const EmployeeSelector = ({
  value,
  onChange,
  label = "Select Employee",
  placeholder = "Search employees...",
  disabled = false,
  required = false,
  helperText = "",
  error = false,
  excludeEmployeeId = null, // Exclude specific employee (e.g., current manager)
  preloadedEmployees = null // Pre-loaded employees to avoid API calls
}) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load employees based on search query
  const loadEmployees = async (query = '') => {
    try {
      setLoading(true);
      // STAFF-S1 — the server returns people in staff only; 500 lifts the default cap of 20.
      const employeesResponse = await departmentService.searchEmployees(query, 500);

      let filteredEmployees = employeesResponse || [];

      // Exclude specific employee if provided
      if (excludeEmployeeId) {
        filteredEmployees = filteredEmployees.filter(emp => emp.id !== excludeEmployeeId);
      }

      setEmployees(filteredEmployees);
    } catch (error) {
      console.error('Error loading employees:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // Load employees on component mount
  useEffect(() => {
    if (preloadedEmployees && preloadedEmployees.length > 0) {
      // Use preloaded employees if available
      let filteredEmployees = preloadedEmployees;
      if (excludeEmployeeId) {
        filteredEmployees = filteredEmployees.filter(emp => emp.id !== excludeEmployeeId);
      }
      setEmployees(filteredEmployees);
    } else {
      loadEmployees();
    }
  }, [preloadedEmployees, excludeEmployeeId]);



  // Generate initials for avatar
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Generate avatar color based on name
  const getAvatarColor = (name) => {
    const colors = [
      '#f44336', '#e91e63', '#9c27b0', '#673ab7',
      '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4',
      '#009688', '#4caf50', '#8bc34a', '#cddc39',
      '#ffeb3b', '#ffc107', '#ff9800', '#ff5722'
    ];

    // Handle undefined or empty name
    if (!name || typeof name !== 'string') {
      return colors[0]; // Return default color
    }

    const hash = name.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  // Find selected employee for display
  const selectedEmployee = employees.find(emp => emp.id === value);
  // STAFF-S1 — offer people in staff; the current value stays visible even if the person has left.
  const visibleEmployees = inStaffOnly(employees, value);



  // Don't render until we have employees loaded (unless it's explicitly empty)
  if (employees.length === 0 && !loading) {
    return (
      <TextField
        fullWidth
        label={label}
        value="Loading employees..."
        disabled
        helperText={helperText}
        error={error}
      />
    );
  }

  return (
    <FormControl fullWidth error={error} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selectedEmployee ? selectedEmployee.id : ''}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        label={label}
        displayEmpty
      >
        <MenuItem value="">
          <em>{placeholder}</em>
        </MenuItem>
        <MenuItem value={null}>
          <em>None</em>
        </MenuItem>
        {visibleEmployees.map((employee) => (
          <MenuItem key={employee.id} value={employee.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: getAvatarColor(getEmployeeFullName(employee) || 'Unknown'),
                  fontSize: '10px',
                  fontWeight: 600
                }}
              >
                {getInitialsFromEmployee(employee)}
              </Avatar>
              <Typography variant="body2">
                {getEmployeeFullName(employee) || 'Unknown Employee'}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          <CircularProgress size={16} />
        </Box>
      )}
    </FormControl>
  );
};

export default EmployeeSelector;
